import { useMutation } from '@apollo/client/react';
import { Grid, TextField, Typography, Paper, Box, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useState, useEffect, useContext } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { ApproveCustomShiftDocument, RejectCustomShiftDocument, type Notification } from '@/graphql/generated/graphql';
import { BLUE0, BLUE1, BLUE2, GREY, GREY1, GREY2 } from '@/theme/palette';
import { getInUTC, isOvernightShift } from '@/utils/dateTimeHelpers';
import { displayNotification } from '@/utils/notifications';

dayjs.extend(utc);

function applyShiftToDate(date: Dayjs, timeStr: string): Dayjs {
  const [h, m] = timeStr.split(':').map(Number);
  return date.hour(h).minute(m).second(0);
}

function applyShiftEndToDate(date: Dayjs, startTimeStr: string, endTimeStr: string): Dayjs {
  const [sh, sm] = startTimeStr.split(':').map(Number);
  const [eh, em] = endTimeStr.split(':').map(Number);
  const isOvernight = eh * 60 + em < sh * 60 + sm;
  const targetDate = isOvernight ? date.add(1, 'day') : date;
  return targetDate.hour(eh).minute(em).second(0);
}

export type CustomShiftActionType = 'approve' | 'reject';

interface CustomShiftActionModalProps {
  open: boolean;
  mode: CustomShiftActionType;
  notification: Notification | null;
  handleClose: () => void;
  onSuccess?: () => void;
}

const sharedMutationOptions = {
  update: (cache: any) => {
    cache.evict({ fieldName: 'getProjectsForScheduling' });
    cache.evict({ fieldName: 'getMyProjects' });
    cache.gc();
  },
  refetchQueries: [
    'getUserNotifications',
    'getProjectsForScheduler',
    'getMyProjects',
    'getSchedulesForScheduler',
    'getSchedules',
  ],
  awaitRefetchQueries: true,
};

const CustomShiftActionModal: React.FC<CustomShiftActionModalProps> = ({
  open,
  mode,
  notification,
  handleClose,
  onSuccess,
}) => {
  const [shiftName, setShiftName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [selectedShiftName, setSelectedShiftName] = useState<string>('');

  const { dateTimePreference } = useContext(CurrentUserContext);

  const details = notification?.customShiftDetails;
  const existingShifts = details?.existingShifts ?? [];
  const hasShifts = existingShifts.length > 0;
  const isApprove = mode === 'approve';

  const formatTime = (timeStr?: string | null) => {
    if (!timeStr) return '';
    if (timeStr.includes('T')) {
      return dayjs(timeStr).format(dateTimePreference.time);
    }
    return dayjs(timeStr, 'HH:mm').format(dateTimePreference.time);
  };

  const renderTimeWithSpan = (startTime?: string | null, endTime?: string | null) => {
    if (!startTime || !endTime) return null;
    const spansMidnight = isOvernightShift(startTime, endTime);
    return (
      <>
        {formatTime(startTime)} – {formatTime(endTime)}
        {spansMidnight && (
          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 600 }}>
            (+1 day)
          </Typography>
        )}
      </>
    );
  };

  useEffect(() => {
    if (open) {
      setShiftName('');
      setNameError('');
      if (hasShifts) {
        setSelectedShiftName(existingShifts[0].name);
      } else {
        setSelectedShiftName('');
      }
    }
  }, [open, mode, notification, hasShifts]);

  const [approveShift, { loading: approveLoading }] = useMutation(ApproveCustomShiftDocument, {
    ...sharedMutationOptions,
    onCompleted: () => {
      displayNotification({
        type: 'success',
        text: 'Custom shift approved and added to project shifts successfully.',
      });
      if (onSuccess) onSuccess();
      handleClose();
    },
    onError: (err) => {
      displayNotification({
        type: 'error',
        text: err.message || 'Failed to approve custom shift.',
      });
    },
  });

  const [rejectShift, { loading: rejectLoading }] = useMutation(RejectCustomShiftDocument, {
    ...sharedMutationOptions,
    onCompleted: () => {
      displayNotification({
        type: 'success',
        text: 'Custom shift rejected and schedule updated successfully.',
      });
      if (onSuccess) onSuccess();
      handleClose();
    },
    onError: (err) => {
      displayNotification({
        type: 'error',
        text: err.message || 'Failed to reject custom shift.',
      });
    },
  });

  if (!open || !notification || !details) return null;

  const loading = approveLoading || rejectLoading;

  const handleApproveSave = () => {
    const trimmed = shiftName.trim();
    if (!trimmed) {
      setNameError('Shift name is required.');
      return;
    }

    const exists = existingShifts.some((s) => s.name.trim().toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setNameError(`A shift named "${trimmed}" already exists for this project.`);
      return;
    }

    approveShift({
      variables: {
        scheduleId: details.scheduleId,
        shiftName: trimmed,
        notificationId: notification.id,
      },
    });
  };

  const handleRejectSave = () => {
    if (!selectedShiftName) return;

    const selectedShift = existingShifts.find((s) => s.name === selectedShiftName);
    if (!selectedShift || !selectedShift.startTime || !selectedShift.endTime) return;

    const baseDate = details.date ? dayjs(details.date) : dayjs();
    const start = applyShiftToDate(baseDate, selectedShift.startTime);
    const end = applyShiftEndToDate(baseDate, selectedShift.startTime, selectedShift.endTime);

    rejectShift({
      variables: {
        scheduleId: details.scheduleId,
        startTime: getInUTC(start),
        endTime: getInUTC(end),
        notificationId: notification.id,
      },
    });
  };

  const isRejectBlocked = !isApprove && !hasShifts;

  return (
    <OverwatchModal
      open={open}
      title={isApprove ? 'Approve Custom Shift' : 'Reject Custom Shift'}
      size="sm"
      handleClose={handleClose}
      hidePrimaryButton={isRejectBlocked}
      primaryButtonText="Save"
      primaryButtonAction={isApprove ? handleApproveSave : handleRejectSave}
      disablePrimary={loading || (isApprove ? !shiftName.trim() : !selectedShiftName)}
      secondaryButtonText={isRejectBlocked ? 'Close' : 'Cancel'}
      secondaryButtonAction={handleClose}
    >
      <Grid container direction="column" spacing={2.5}>
        {/* Custom Shift Details Card */}
        <Grid>
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: '8px',
              bgcolor: BLUE2,
              border: `1px solid ${BLUE1}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Project
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {details.projectName}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {isApprove ? 'Custom Shift Hours' : 'Requested Shift Hours'}
              </Typography>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.35,
                  borderRadius: '6px',
                  bgcolor: 'background.paper',
                  border: `1px solid ${BLUE1}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" fontWeight={700} color={BLUE0}>
                  {renderTimeWithSpan(details.startTime, details.endTime)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {isApprove ? (
          /* APPROVE FLOW */
          <>
            <Grid>
              <TextField
                autoFocus
                label="Shift Name"
                placeholder="e.g. Day Shift, Split Shift"
                value={shiftName}
                onChange={(e) => {
                  setShiftName(e.target.value);
                  if (nameError) setNameError('');
                }}
                error={Boolean(nameError)}
                helperText={nameError}
                fullWidth
                required
                size="small"
              />
            </Grid>

            {/* Existing Project Shifts Section */}
            <Grid>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Existing Project Shifts (Read-only)
              </Typography>
              {hasShifts ? (
                <Box
                  sx={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    pr: 0.5,
                  }}
                >
                  {existingShifts.map((shift, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: '8px',
                        border: `1px solid ${GREY1}`,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'background-color 0.15s ease-in-out',
                        '&:hover': {
                          bgcolor: GREY2,
                        },
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {shift.name}
                      </Typography>
                      <Typography variant="body2" color={GREY}>
                        {renderTimeWithSpan(shift.startTime, shift.endTime)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box p={2} borderRadius="8px" border={`1px dashed ${GREY1}`} textAlign="center" bgcolor={GREY2}>
                  <Typography variant="body2" color="text.secondary">
                    No shifts currently defined for this project.
                  </Typography>
                </Box>
              )}
            </Grid>
          </>
        ) : /* REJECT FLOW */
        !hasShifts ? (
          <Grid>
            <Box p={2.5} borderRadius="8px" border={`1px dashed ${GREY1}`} textAlign="center" bgcolor={GREY2}>
              <Typography variant="body2" color="text.secondary">
                Before rejecting this shift, please create at least one project shift for the project.
              </Typography>
            </Box>
          </Grid>
        ) : (
          <Grid>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Select a project shift to apply:
            </Typography>
            <RadioGroup value={selectedShiftName} onChange={(e) => setSelectedShiftName(e.target.value)}>
              <Grid container direction="column" spacing={1} sx={{ maxHeight: 200, overflowY: 'auto', pr: 0.5 }}>
                {existingShifts.map((shift, idx) => {
                  const isSelected = selectedShiftName === shift.name;
                  return (
                    <Grid key={idx}>
                      <Paper
                        variant="outlined"
                        onClick={() => setSelectedShiftName(shift.name)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: '8px',
                          border: isSelected ? `2px solid ${BLUE0}` : `1px solid ${GREY1}`,
                          bgcolor: isSelected ? 'action.hover' : 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.15s ease-in-out',
                        }}
                      >
                        <FormControlLabel
                          value={shift.name}
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2" fontWeight={600}>
                              {shift.name}
                            </Typography>
                          }
                          sx={{ m: 0, flexGrow: 1 }}
                        />
                        <Typography variant="body2" color={GREY}>
                          {renderTimeWithSpan(shift.startTime, shift.endTime)}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          </Grid>
        )}
      </Grid>
    </OverwatchModal>
  );
};

export default CustomShiftActionModal;
